import { useEffect, useRef, useCallback, useState } from "react";

const TOTAL_MS   = 60 * 60 * 1000; // 60 minutes total
const WARNING_MS =  2 * 60 * 1000; // warn 2 minutes before logout

/**
 * Logs the user out after TOTAL_MS of inactivity.
 * Shows a warning TOTAL_MS - WARNING_MS into the idle period.
 * User activity (click, keydown, mousemove, scroll, touch) resets the clock,
 * UNLESS the warning modal is already visible — then only "Stay Logged In"
 * can reset it, preventing an accidental dismissal by mouse movement.
 *
 * @param {Object} opts
 * @param {boolean} opts.enabled  - set false to disable (e.g. on admin / tenant portal)
 * @param {Function} opts.onLogout - called when the timeout expires
 * @returns {{ showWarning: boolean, stayLoggedIn: Function }}
 */
export function useInactivityTimeout({ enabled = true, onLogout }) {
  const [showWarning, setShowWarning] = useState(false);
  const warningTimerRef = useRef(null);
  const logoutTimerRef  = useRef(null);
  const warnedRef       = useRef(false); // true while warning modal is visible

  const clearAll = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    clearAll();
    warnedRef.current = false;
    setShowWarning(false);

    warningTimerRef.current = setTimeout(() => {
      warnedRef.current = true;
      setShowWarning(true);
      // After the warning period, log out
      logoutTimerRef.current = setTimeout(onLogout, WARNING_MS);
    }, TOTAL_MS - WARNING_MS);
  }, [enabled, clearAll, onLogout]);

  useEffect(() => {
    if (!enabled) { clearAll(); return; }

    const onActivity = () => {
      // If the warning is visible, only the explicit "Stay Logged In" button
      // should reset the timer — mouse moves or stray clicks shouldn't dismiss it.
      if (warnedRef.current) return;
      resetTimer();
    };

    const events = ["click", "keydown", "mousemove", "scroll", "touchstart"];
    events.forEach(ev => document.addEventListener(ev, onActivity, { passive: true }));
    resetTimer(); // start the clock on mount

    return () => {
      clearAll();
      events.forEach(ev => document.removeEventListener(ev, onActivity));
    };
  }, [enabled, resetTimer, clearAll]);

  /** Call this when the user clicks "Stay Logged In" in the warning modal. */
  const stayLoggedIn = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  return { showWarning, stayLoggedIn };
}
